import { motion } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { useLightbox } from "@/components/Lightbox";
import { useSlowFactor } from "@/hooks/useSlowFactor";
import { useLanguage } from "@/i18n/LanguageContext";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/599d8969-9f91-429c-bb16-01c12d212da1/images/7a78cd007d0ee95c43db5a12cfa9e354e379c0a12ff687d9398964cd42c87fb1.png";

export const Hero = () => {
  const { open } = useLightbox();
  const slow = useSlowFactor();
  const { t } = useLanguage();
  return (
    <section id="top" data-testid="hero-section" className="relative pt-20 carbon">
      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-5rem)]">
        {/* Left — content */}
        <div className="flex items-center px-4 sm:px-6 lg:px-12 xl:px-20 py-16 lg:py-0">
          <div className="max-w-xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.55 * slow, ease: "easeOut" }}
              className="text-sm font-bold uppercase tracking-[0.25em] text-red-500 mb-6 flex items-center gap-3"
            >
              <span className="w-8 h-px bg-red-500" /> {t.hero.location}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.9 * slow, delay: 0.45 * slow, ease: "easeOut" }}
              className="text-5xl sm:text-6xl md:text-7xl font-bold uppercase tracking-tighter leading-[1.02] text-white"
            >
              {t.hero.titlePre} <span className="text-red-500">{t.hero.titleAccent}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.9 * slow, delay: 0.85 * slow, ease: "easeOut" }}
              className="mt-8 text-lg sm:text-xl text-zinc-300 leading-relaxed"
            >
              {t.hero.subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.9 * slow, delay: 1.25 * slow, ease: "easeOut" }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href="tel:+359892600604"
                data-testid="hero-cta-call"
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white px-8 py-4 font-bold uppercase tracking-wider transition-colors shadow-[0_0_30px_rgba(239,68,68,0.45)]"
              >
                <Phone size={18} strokeWidth={2.5} /> +359 892 600 604
              </a>
              <a
                href="#services"
                data-testid="hero-cta-services"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-zinc-700 hover:border-red-500 hover:text-red-500 text-white px-8 py-4 font-bold uppercase tracking-wider transition-colors"
              >
                {t.hero.ctaServices} <ArrowRight size={18} strokeWidth={2.5} />
              </a>
            </motion.div>

            <div className="mt-14 grid grid-cols-3 gap-4 border-t border-zinc-800 pt-8">
              {t.hero.stats.map((s) => (
                <div key={s.l}>
                  <p className="font-display font-bold text-3xl text-red-500">{s.n}</p>
                  <p className="text-xs uppercase tracking-wide text-zinc-300 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right — image */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1 * slow }}
          onClick={() => open(HERO_IMG, t.hero.imgAlt)}
          className="relative min-h-[400px] lg:min-h-full bg-black photo-frame cursor-pointer group overflow-hidden"
        >
          <img
            src={HERO_IMG}
            alt={t.hero.imgAlt}
            className="absolute inset-0 w-full h-full object-cover brightness-[1.85] transition-transform duration-[600ms] ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />
          <div className="hidden lg:block absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-[#0a0a0b] to-transparent" />
          <div className="absolute bottom-0 left-0 bg-red-600 text-white px-4 py-2 max-w-[260px] shadow-[0_0_40px_rgba(239,68,68,0.5)]">
            <p className="font-display uppercase font-bold tracking-tight text-base leading-none">
              {t.hero.badgeTitle}
            </p>
            <p className="text-xs text-red-100 mt-1 leading-tight">{t.hero.badgeDesc}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
